/**
 * GlobalAddTodo — "+ To-do" in the top bar: anyone can add a to-do from
 * anywhere (the to-dos upgrade, 2026-10-04; Michael: "the one thing I'd add is
 * a '+ add a todo' at the top nav bar so anyone can add a todo from anywhere").
 *
 * It starts FILLED IN from the page it was pressed on — on a deal, the deal's
 * client, the deal, its contact and Sales; on a project, Ops and the project's
 * primary contact; on a ticket, a client file, a contact, an L10, an issue or a
 * rock likewise ("depending on where this todo is coming from, it'll auto
 * populate the information — like client, project, contact, team"). The page
 * is read off the URL (`pageRecordOf`, THE GATE's canonical routes) and the
 * server fills in the rest (GET /api/todos/context). Everything stays editable.
 *
 * THE PERMISSION GATE: creating a to-do needs the operations tab and
 * `action:manage_todos` (routes/todos.ts) — without them the button is absent,
 * not greyed.
 */
import React, { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Tooltip } from '@mui/material';
import { HubButton } from '../clients/hub/designSystem';
import TodoCreateHost from './TodoCreateHost';
import type { TodoCreatePrefill } from './TodoCreateContainer';
import { pageRecordOf, TODO_CREATED_EVENT } from './todoPageContext';
import { todosApi, type TodoPageContext, type TodoRow } from '../../services/todosApi';
import { useAuth } from '../../contexts/AuthContext';

export function prefillFromContext(ctx: TodoPageContext): TodoCreatePrefill {
  return {
    client_card_id: ctx.client_card_id,
    client_name: ctx.client_name,
    record: ctx.record
      ? {
          kind: ctx.record.kind,
          id: ctx.record.id,
          label: ctx.record.label,
          source_type: ctx.record.source_type,
          source_id: ctx.record.source_id,
          ticket_id: ctx.record.ticket_id ?? null,
        }
      : null,
    contact: ctx.contact,
    ...(ctx.team ? { team: ctx.team } : {}),
    department: ctx.department ?? null,
  };
}

export default function GlobalAddTodo(): React.ReactElement | null {
  const auth = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [prefill, setPrefill] = useState<TodoCreatePrefill | null>(null);
  const [busy, setBusy] = useState(false);

  const start = useCallback(async () => {
    if (busy) return;
    const ref = pageRecordOf(location.pathname);
    let next: TodoCreatePrefill = {};
    if (ref) {
      setBusy(true);
      try {
        const res = await todosApi.pageContext(ref.kind, ref.id);
        if (res.data) next = prefillFromContext(res.data);
      } catch {
        // Not a record this person can read (or it is gone): start blank
        // rather than refuse — the to-do is still worth writing down.
      } finally {
        setBusy(false);
      }
    }
    setPrefill(next);
    setOpen(true);
  }, [busy, location.pathname]);

  const onCreated = useCallback((row: TodoRow) => {
    try { window.dispatchEvent(new CustomEvent(TODO_CREATED_EVENT, { detail: { id: row.id } })); } catch { /* old browsers */ }
  }, []);

  if (!auth.user || !auth.hasTab('operations') || !auth.hasAction('manage_todos')) return null;

  return (
    <>
      <Tooltip title="Add a to-do — it starts filled in from the page you are on">
        <span style={{ marginLeft: 6, marginRight: 2, display: 'inline-flex' }}>
          <HubButton variant="primary" onClick={() => { void start(); }} disabled={busy}>
            + To-do
          </HubButton>
        </span>
      </Tooltip>
      <TodoCreateHost
        open={open}
        prefill={prefill}
        onClose={() => setOpen(false)}
        onCreated={onCreated}
        announce
      />
    </>
  );
}
